export default {
  slug: "construction-tickets-licences-australia",
  category: "Tickets & Licences",
  seoTitle: "Construction Tickets & Licences in Australia: What Do You Need?",
  description:
    "Which construction tickets and licences do you actually need in Australia? White Card, forklift, working at heights, EWP, traffic management, high-risk work licences — plus how to verify requirements before paying for training.",
  h1: "Construction Tickets & Licences in Australia: What Do You Need?",
  readTime: "13 min read",
  lastReviewed: "September 2026",
  intro:
    "Australian construction runs on tickets. Some are legal requirements to step on site, some are licences for high-risk work, and some are simply tickets employers like to see. The expensive mistake is buying training before you know which category your target role falls into. This guide maps the landscape — and shows you how to verify any requirement with an authoritative source before you spend a dollar.",
  calloutTop: {
    title: "Read this first",
    text: "Requirements vary by state, territory, role and employer. Training units are periodically superseded and renamed. Nothing here is a substitute for checking the current requirement for your specific job — every section below tells you where to verify.",
  },
  sections: [
    {
      h: "The White Card (general construction induction)",
      body: [
        "The White Card is the baseline legal requirement for construction work across Australia — you need it before working on a construction site in any state or territory. It is issued after completing general construction induction training through a registered training organisation (RTO). The current unit of competency can be verified on training.gov.au (units are occasionally superseded, so always confirm the current code).",
        "Delivery rules differ by jurisdiction: some states allow fully online delivery, others require face-to-face or live video assessment. Your state or territory WHS regulator — SafeWork NSW, WorkSafe Victoria, WorkSafe QLD, WorkSafe WA, SafeWork SA or their equivalents — publishes the accepted delivery modes. Safe Work Australia provides the national framework.",
      ],
    },
    {
      h: "Driver's licence",
      body: [
        "Not a site ticket, but close to mandatory in practice: early starts, poor public transport coverage and rotating sites mean most employers ask for a current licence, and many prefer manual. If a role involves driving work vehicles, the class required depends on the vehicle. Some site driving roles additionally require specific vehicle classes under state licensing rules — check with your state transport authority.",
      ],
    },
    {
      h: "High-risk work licences",
      body: [
        "Certain work is licensed under WHS law nationwide, with licences issued by state and territory regulators. Common construction examples include:",
      ],
      list: [
        "Forklift (LF class) — operating a forklift truck",
        "EWP / boom lift (WP class) — required for boom-type elevating work platforms with a boom length of 11 metres or more; scissor lifts and smaller booms follow employer and manufacturer requirements rather than a national licence",
        "Rigging and dogging (various classes) — directing crane loads and moving plant",
        "Crane operation (various classes)",
        "Scaffolding (basic through advanced classes)",
      ],
      body2: [
        "High-risk work licences require training and assessment through an RTO, then a licence application to your state or territory regulator — in Western Australia, that is WorkSafe WA. Each class has its own prerequisites and costs. These are genuine career-building licences, but they are significant investments: get one when a role calls for it, not before.",
      ],
    },
    {
      h: "Common site tickets that aren't licences",
      body: [
        "A large category of training is widely requested but not a statutory licence. The need depends entirely on the role and employer:",
      ],
      list: [
        "Working at heights — standard for roof work, many civil and resources roles",
        "Confined space entry — common in drainage, tank and resources work",
        "Traffic management / traffic control — required for traffic control duties; training and accreditation are state-specific (e.g. different systems in NSW, Victoria, Queensland and WA)",
        "First aid — valued everywhere, sometimes required for leading hands and remote crews",
        "Plant 'tickets' (e.g. excavator, roller, skid steer) — competency-based; requirements sit with the employer and site rules rather than a single national licence for most plant",
      ],
      callout: {
        title: "Golden rule",
        text: "No ticket guarantees employment, and no employer should ask you to pay for training as a condition of a job offer through an unverified third party. Verify the requirement, then verify the provider.",
      },
    },
    {
      h: "State and territory differences",
      body: [
        "While WHS laws are largely harmonised, delivery rules, accepted training modes, traffic control accreditation and some licensing processes differ between jurisdictions. A White Card is recognised nationally; traffic control credentials generally are not. If you plan to work across borders, check both regulators' websites before assuming a ticket transfers.",
      ],
    },
    {
      h: "What's actually worth getting as a beginner",
      body: [
        "For most entry-level job seekers, the sensible order is: White Card first (essential), driver's licence progress (essential in practice), and then — only after registering with labour hire agencies and seeing what local roles actually request — consider one additional ticket that keeps appearing in ads you could realistically win. That demand-driven approach saves most beginners hundreds of dollars.",
      ],
    },
    {
      h: "How to verify a requirement before paying",
      list: [
        "Find three current job ads for the exact role you want and note the tickets they actually list",
        "Confirm the current unit of competency and course name on training.gov.au",
        "Check your state or territory WHS regulator for licensing classes and accepted delivery (WorkSafe WA for Western Australia)",
        "Confirm the training provider is a registered training organisation before paying",
        "Ask a labour hire coordinator what their clients are currently requesting — they see demand daily",
      ],
    },
    {
      h: "Avoiding unnecessary training costs",
      body: [
        "The pattern to avoid: paying for a 'job-ready package' of five tickets before speaking to a single employer. Some of the most expensive beginner tickets are the least requested for entry roles, while the genuinely requested ones are often cheap or employer-provided after hire. Some employers and group training organisations also fund inductions and tickets for the right candidates — ask.",
      ],
    },
  ],
  sources: [
    { label: "Safe Work Australia — national WHS framework", url: "https://www.safeworkaustralia.gov.au" },
    { label: "training.gov.au — current units of competency", url: "https://training.gov.au" },
    { label: "WorkSafe WA — licences in Western Australia", url: "https://www.worksafe.wa.gov.au" },
    { label: "SafeWork NSW", url: "https://www.safework.nsw.gov.au" },
    { label: "WorkSafe Victoria", url: "https://www.worksafe.vic.gov.au" },
    { label: "WorkSafe Queensland", url: "https://www.worksafe.qld.gov.au" },
  ],
  cta: "The Sitebound Complete Construction Job Seeker Pack includes a tickets roadmap and cost-avoidance checklist that maps tickets to roles — so you only pay for what your target job actually asks for.",
  related: ["construction-jobs-no-experience-australia", "fifo-jobs-no-experience-australia", "construction-resume-australia"],
};
